import { useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { toast, Toaster } from 'sonner';
import { useTranslation } from 'react-i18next';
import { queryClient } from './lib/queryClient';
import { useAuthStore } from './store/authStore';
import { useUploadStore } from './store/uploadStore';
import { Spinner } from './components/ui/Spinner';
import { ProtectedRoute } from './components/layout/ProtectedRoute';
import { NavigationGuard } from './components/layout/NavigationGuard';
import { ScrollToTop } from './components/layout/ScrollToTop';
import { usePlatformEvents, type PlatformEvent } from './hooks/usePlatformEvents';

const HomePage = lazy(() => import('./pages/HomePage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const OutdoorDetailPage = lazy(() => import('./pages/OutdoorDetailPage'));
const BusinessSetupPage = lazy(() => import('./pages/BusinessSetupPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ManageOutdoorsPage = lazy(() => import('./pages/ManageOutdoorsPage'));
const OutdoorEditPage = lazy(() => import('./pages/OutdoorEditPage'));
const CampaignOutdoorVerificationPage = lazy(() => import('./pages/CampaignOutdoorVerificationPage'));
const PlacementProviderCampaignsPage = lazy(() => import('./pages/PlacementProviderCampaignsPage'));
const PlacementProviderCampaignDetailPage = lazy(() => import('./pages/PlacementProviderCampaignDetailPage'));
const OfflineCampaignDetailPage = lazy(() => import('./pages/OfflineCampaignDetailPage'));
const UpcomingCampaignsPage = lazy(() => import('./pages/UpcomingCampaignsPage'));
const GetPaidPage = lazy(() => import('./pages/GetPaidPage'));
const ApprovalsPage = lazy(() => import('./pages/ApprovalsPage'));
const ApprovalReviewPage = lazy(() => import('./pages/ApprovalReviewPage'));
const CampaignsPage = lazy(() => import('./pages/CampaignsPage'));
const CampaignDetailPage = lazy(() => import('./pages/CampaignDetailPage'));
const CampaignPaymentReturnPage = lazy(() => import('./pages/CampaignPaymentReturnPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const AdminRevenuePage = lazy(() => import('./pages/AdminRevenuePage'));
const CompanyDetailsPage = lazy(() => import('./pages/CompanyDetailsPage'));
const UserDetailsPage = lazy(() => import('./pages/UserDetailsPage'));
const InvoicesPage = lazy(() => import('./pages/InvoicesPage'));
const PaymentsPage = lazy(() => import('./pages/PaymentsPage'));
const TeamManagementPage = lazy(() => import('./pages/TeamManagementPage'));
const ResendVerificationPage = lazy(() => import('./pages/ResendVerificationPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const HowItWorksPage = lazy(() => import('./pages/HowItWorksPage'));
const OurMissionPage = lazy(() => import('./pages/OurMissionPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));

const PageSpinner = () => (
  <div className="flex h-screen items-center justify-center">
    <Spinner size="lg" />
  </div>
);

const GlobalUploadIndicator = () => {
  const { t } = useTranslation();
  const pendingCount = useUploadStore((s) => s.pendingCount);
  const toastIdRef = useRef<string | number | null>(null);

  useEffect(() => {
    if (pendingCount > 0 && toastIdRef.current === null) {
      toastIdRef.current = toast.loading(t('common.uploading'));
    } else if (pendingCount === 0 && toastIdRef.current !== null) {
      toast.dismiss(toastIdRef.current);
      toastIdRef.current = null;
    }
  }, [pendingCount, t]);

  return null;
};

const PlatformEventsSync = () => {
  const qc = useQueryClient();
  const rehydrate = useAuthStore((s) => s.rehydrate);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const onEvent = useCallback((event: PlatformEvent) => {
    // Image-processing outcome events (upload -> automated/manual review -> final status) can
    // reach the client a moment before the backend's own read model is fully consistent with
    // that outcome - the first refetch below can land mid-transition and keep showing the old
    // "processing" state until something else happens to invalidate the query again (e.g. a
    // manual page refresh, moments later, once the backend has caught up). A second, delayed
    // invalidation closes that gap without requiring the user to do anything.
    const invalidateSoonAfter = (keys: Array<Array<string>>) => {
      keys.forEach((queryKey) => qc.invalidateQueries({ queryKey }));
      setTimeout(() => {
        keys.forEach((queryKey) => qc.invalidateQueries({ queryKey }));
      }, 1500);
    };

    switch (event.type) {
      case 'OUTDOOR_IMAGE':
        useUploadStore.getState().finish();
        invalidateSoonAfter([['outdoor'], ['my-outdoors-cards'], ['my-outdoors-table']]);
        toast.success(event.message);
        break;

      case 'AD_IMAGE':
        useUploadStore.getState().finish();
        useUploadStore.getState().notifyAdImage();
        invalidateSoonAfter([
          // Refresh the campaign detail (creatives section) for the advertiser
          ['campaign'],
          // Refresh admin verification queue in case the creative landed in MANUAL_VALIDATION
          ['admin-pending-ads'],
          ['admin-section-counts'],
        ]);
        toast.success(event.message);
        break;

      case 'PROOF_OF_AD':
        useUploadStore.getState().finish();
        // Tells Verify Displays pages (GetPaidPage, CampaignOutdoorVerificationPage) to drop
        // their locally-remembered "just uploaded" state and trust the refetched server status -
        // e.g. when a proof is later found INVALID, this clears the "Uploaded" badge and
        // re-enables the upload button for that outdoor.
        useUploadStore.getState().notifyProofOfAd();
        invalidateSoonAfter([
          // PlacementProviderCampaignDetailPage - slot proof state
          ['pp-campaign-outdoors'],
          ['campaign'],
          // CampaignOutdoorVerificationPage - verification list
          ['campaign-outdoors-by-provider'],
          ['placement-campaigns-to-verify'],
          // GetPaidPage - proof status & campaign list
          ['gp-verify-outdoors'],
          ['gp-verify-campaigns'],
          // UpcomingCampaignsPage
          ['pp-ready-campaigns'],
          // Admin verification queue & badge
          ['admin-pending-proof-of-ads'],
          ['admin-section-counts'],
        ]);
        toast.success(event.message);
        break;

      case 'PROOF_OF_COMPANY':
        useUploadStore.getState().finish();
        invalidateSoonAfter([['business-entity']]);
        break;

      case 'MANUAL_APPROVAL_PROOF_OF_COMPANY':
        // Proof was routed to manual review - refetch so the company details page's
        // upload card switches from the upload form to the "being reviewed" state.
        invalidateSoonAfter([['business-entity']]);
        break;

      case 'APPROVED_PROOF_OF_COMPANY':
        rehydrate();
        invalidateSoonAfter([['business-entity']]);
        break;

      case 'APPROVED_ACCESS_REQUEST':
        // The current access token carries no entity claims yet - force a fresh one
        // so the next rehydrate() call gets the updated businessEntityId from the API.
        localStorage.removeItem('accessToken');
        rehydrate();
        qc.invalidateQueries({ queryKey: ['business-entity'] });
        qc.invalidateQueries({
          predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === 'access-request-by-user',
        });
        toast.success(t('business.setup.accessApproved'));
        break;

      case 'REJECTED_ACCESS_REQUEST':
        localStorage.removeItem('accessToken');
        rehydrate();
        qc.invalidateQueries({
          predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === 'access-request-by-user',
        });
        toast.error(t('business.setup.accessRejected'));
        navigate('/business/setup');
        break;

      case 'INVOICE':
        qc.invalidateQueries({ queryKey: ['invoices-page'] });
        break;

      case 'CAMPAIGN_FEEDBACK_RECEIVED':
      case 'CAMPAIGN_CONFIRMATION_UPDATED': {
        // Providers' answers changed the campaign's outdoors (and maybe its status, costs and payments): refresh
        // its page, the campaigns list and the payment breakdown wherever they're shown.
        const id = event.campaignId;
        invalidateSoonAfter(id
          ? [['campaign', id], ['campaign-outdoor-costs', id], ['campaigns']]
          : [['campaign'], ['campaign-outdoor-costs'], ['campaigns']]);
        // The backend's message is English-only - show our translated one, with a way to the campaign unless
        // the advertiser is already looking at it.
        const onThatPage = !!id && window.location.pathname === `/campaigns/${id}`;
        toast.info(
          event.type === 'CAMPAIGN_FEEDBACK_RECEIVED' ? t('campaign.eventFeedbackReceived') : t('campaign.eventConfirmationUpdated'),
          id && !onThatPage
            ? { action: { label: t('campaign.eventViewCampaign'), onClick: () => navigate(`/campaigns/${id}`) } }
            : undefined,
        );
        break;
      }

      case 'CHAT_MESSAGE':
        qc.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            (query.queryKey[0] === 'campaign-messages' ||
              query.queryKey[0] === 'pp-campaign-messages' ||
              query.queryKey[0] === 'campaign-message-projections'),
        });
        break;
    }
  }, [qc, navigate, t, rehydrate]);

  usePlatformEvents(onEvent);
  return null;
};

const AppRoutes = () => {
  const rehydrate = useAuthStore((s) => s.rehydrate);

  useEffect(() => {
    rehydrate();
  }, [rehydrate]);

  return (
    <Suspense fallback={<PageSpinner />}>
      <ScrollToTop />
      <NavigationGuard />
      <PlatformEventsSync />
      <GlobalUploadIndicator />
      <Routes>
        {/* Public */}
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/verify/:verificationId" element={<VerifyEmailPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:userId/:passwordResetId" element={<ResetPasswordPage />} />
        <Route path="/resend-verification" element={<ResendVerificationPage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/outdoors/:id" element={<OutdoorDetailPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/how-it-works" element={<HowItWorksPage />} />
        <Route path="/our-mission" element={<OurMissionPage />} />
        <Route path="/contact" element={<ContactPage />} />

        {/* Account details - any authenticated user, business or not */}
        <Route
          path="/account/details"
          element={
            <ProtectedRoute>
              <UserDetailsPage />
            </ProtectedRoute>
          }
        />

        {/* Authenticated, needs business setup - admins have no business entity */}
        <Route
          path="/business/setup"
          element={
            <ProtectedRoute noAdmin>
              <BusinessSetupPage />
            </ProtectedRoute>
          }
        />

        {/* Dashboard - any business type */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute requireBusiness requireVerifiedBusiness>
              <DashboardPage />
            </ProtectedRoute>
          }
        />

        {/* Company details */}
        <Route
          path="/business/details"
          element={
            <ProtectedRoute requireBusiness>
              <CompanyDetailsPage />
            </ProtectedRoute>
          }
        />

        {/* Invoices */}
        <Route
          path="/invoices"
          element={
            <ProtectedRoute requireBusiness requireVerifiedBusiness>
              <InvoicesPage />
            </ProtectedRoute>
          }
        />

        {/* Team Management */}
        <Route
          path="/team"
          element={
            <ProtectedRoute requireBusiness requireVerifiedBusiness>
              <TeamManagementPage />
            </ProtectedRoute>
          }
        />

        {/* Placement Provider routes */}
        <Route
          path="/outdoors/manage"
          element={
            <ProtectedRoute requiredType="PLACEMENT_PROVIDER" requireVerifiedBusiness>
              <ManageOutdoorsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/outdoors/manage/:id"
          element={
            <ProtectedRoute requiredType="PLACEMENT_PROVIDER" requireVerifiedBusiness>
              <OutdoorEditPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/outdoors/verify"
          element={
            <ProtectedRoute requiredType="PLACEMENT_PROVIDER" requireVerifiedBusiness>
              <CampaignOutdoorVerificationPage />
            </ProtectedRoute>
          }
        />

        {/* Placement Provider - campaigns */}
        <Route
          path="/pp/upcoming"
          element={
            <ProtectedRoute requiredType="PLACEMENT_PROVIDER" requireVerifiedBusiness>
              <UpcomingCampaignsPage />
            </ProtectedRoute>
          }
        />
        {/* Placement Provider - approving their outdoors in paid campaigns (emails link to /pp/approvals/:campaignId) */}
        <Route
          path="/pp/approvals"
          element={
            <ProtectedRoute requiredType="PLACEMENT_PROVIDER" requireVerifiedBusiness>
              <ApprovalsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pp/approvals/:campaignId"
          element={
            <ProtectedRoute requiredType="PLACEMENT_PROVIDER" requireVerifiedBusiness>
              <ApprovalReviewPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pp/get-paid"
          element={
            <ProtectedRoute requiredType="PLACEMENT_PROVIDER" requireVerifiedBusiness>
              <GetPaidPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pp/campaigns"
          element={
            <ProtectedRoute requiredType="PLACEMENT_PROVIDER" requireVerifiedBusiness>
              <PlacementProviderCampaignsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pp/payments"
          element={
            <ProtectedRoute requiredType="PLACEMENT_PROVIDER" requireVerifiedBusiness>
              <PaymentsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pp/campaigns/offline/:id"
          element={
            <ProtectedRoute requiredType="PLACEMENT_PROVIDER" requireVerifiedBusiness>
              <OfflineCampaignDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pp/campaigns/:id"
          element={
            <ProtectedRoute requiredType="PLACEMENT_PROVIDER" requireVerifiedBusiness>
              <PlacementProviderCampaignDetailPage />
            </ProtectedRoute>
          }
        />

        {/* Advertiser routes */}
        <Route
          path="/campaigns"
          element={
            <ProtectedRoute requiredType="ADVERTISER" requireVerifiedBusiness>
              <CampaignsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/campaigns/:id/check-payment"
          element={
            <ProtectedRoute requiredType="ADVERTISER" requireVerifiedBusiness>
              <CampaignPaymentReturnPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/campaigns/:id"
          element={
            <ProtectedRoute requiredType="ADVERTISER" requireVerifiedBusiness>
              <CampaignDetailPage />
            </ProtectedRoute>
          }
        />

        {/* Admin */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute requireAdmin>
              <AdminPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/revenue"
          element={
            <ProtectedRoute requireAdmin>
              <AdminRevenuePage />
            </ProtectedRoute>
          }
        />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <AppRoutes />
      <Toaster
        position="bottom-right"
        richColors
        toastOptions={{
          style: { borderRadius: '12px' },
        }}
      />
    </BrowserRouter>
  </QueryClientProvider>
);

export default App;
